import { useMemo, useState, type Dispatch } from "react";
import { useTranslation } from "react-i18next";
import { type TDialCodes, useDialCodes } from "@src/api/useDialCodes";
import chevronDown from "@src/assets/icons/chevronDown.svg";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
} from "@src/components/ui/dialog";
import { Input } from "@src/components/ui/input";
import { cn } from "@src/lib/utils";

interface TProp {
  dialCode: string;
  setCountry: Dispatch<TDialCodes>;
}

function PhoneSelect({ dialCode = "995", setCountry }: TProp) {
  const { t } = useTranslation();
  const countries = useDialCodes();
  const [keyword, setKeyword] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const memoCountry = useMemo(
    () => countries.data?.countries.find((c) => c.dialCode === dialCode),
    [countries.data?.countries, dialCode]
  );

  const openDialog = () => {
    setIsDialogOpen(true);
  };
  const closeDialog = () => {
    setIsDialogOpen(false);
  };

  const searchedCountries = useMemo(() => {
    const listOfCountries = countries.data?.countries.filter((c) =>
      c.countryName?.toLowerCase().includes(keyword.toLowerCase())
    );
    if (listOfCountries?.length) {
      return listOfCountries;
    }
    return [];
  }, [countries.data?.countries, keyword]);

  return (
    <Dialog onOpenChange={closeDialog} open={isDialogOpen}>
      <div>
        { }
        <div
          className="flex h-14 w-32 cursor-pointer items-center justify-between rounded-lg border border-solid border-gray-200 px-4 py-2"
          onClick={openDialog}
        >
          <div>
            {memoCountry ? (
              <div className="flex items-center gap-1">
                <img
                  alt="chevronDown"
                  className="h-6 w-6 rounded-full"
                  src={memoCountry.flagURL}
                />
                <p className="text-sm text-gray-700">
                  + {memoCountry.dialCode}
                </p>
              </div>
            ) : null}
          </div>
          <img
            alt="chevronDown"
            className="h-4 w-4 rounded-full"
            src={chevronDown}
          />
        </div>
      </div>
      <DialogContent
        className={cn("w-[375px] rounded-xl bg-white p-0 md:w-full", searchedCountries.length > 0 ? "h-auto" : "h-[250px]")}
        withoutX
      >
        <DialogHeader>
          <Input
            className="border-b border-solid border-gray-200 p-4"
            onChange={(e) => {
              setKeyword(e.target.value);
            }}
            placeholder={t("search")}
            value={keyword}
          />
          <DialogDescription className="h-[364px] overflow-auto">
            {searchedCountries.length > 0 ? (
              searchedCountries.map((c) => {
                return (
                   
                  <div
                    className={cn(
                      "flex h-[52px] cursor-pointer  items-center gap-2 border-b border-solid border-gray-200 px-4 py-2 text-left",
                      c.dialCode === dialCode &&
                        "border-r-whitebitYellow border-r-4 border-solid"
                    )}
                    key={c.dialCode}
                    onClick={() => {
                      closeDialog();
                      setCountry(c);
                    }}
                  >
                    <img
                      alt="logo"
                      className="h-5 w-5 rounded-full object-contain"
                      src={c.flagURL}
                    />
                    <p className="text-textGray">{c.countryName}</p>
                    <p className="text-xs text-gray-400">(+{c.dialCode})</p>
                  </div>
                );
              })
            ) : (
              <div className="flex flex-col items-center gap-3 p-4 text-center">
                <div className="my-6 flex max-w-[400px] flex-col gap-2">
                  <p className="text-xl font-bold text-baseBlack">
                    {t("not_found_title")}
                  </p>
                  <p className="mt-2 leading-6 text-textBlack">
                    {t("not_found_desc")}
                  </p>
                </div>
              </div>
            )}
          </DialogDescription>
        </DialogHeader>
      </DialogContent>
    </Dialog>
  );
}

export default PhoneSelect;
